import { useOnboarding, PRICING } from "./OnboardingContext";
import { ChevronDown, ChevronUp, Package, Settings, Droplets, Calendar, TrendingUp, Clock, Tag, Phone } from "lucide-react";
import { useState } from "react";

const SummaryPanel = () => {
  const {
    selectedPackage,
    selectedDistributionTier,
    numberOfMachines,
    selectedAddOns,
    monthlyPrice,
    discountedPrice,
    setupFee,
    promoApplied,
    promoCode,
    promoDiscount,
    promoMonths,
    getPackageDetails,
    getDistributionTierDetails
  } = useOnboarding();

  const [isExpanded, setIsExpanded] = useState(false);
  const pkg = getPackageDetails();
  const tier = getDistributionTierDetails();

  if (!pkg) return null;

  const additionalMachines = Math.max(0, numberOfMachines - 1);

  const tierPrice = tier?.price || 0;
  const machinesCost = additionalMachines * pkg.basePrice;
  const addOnsCost = selectedAddOns.reduce((sum, id) => sum + (PRICING.addOns[id]?.price || 0), 0);

  return (
    <>
      {/* Desktop Sticky Panel */}
      <div
        data-testid="summary-panel-desktop"
        className="hidden lg:block sticky top-24 bg-white border border-[#E5E5E5] p-6 z-10"
      >
        {/* Pre-order Badge */}
        <div className="flex items-center gap-2 mb-4">
          <Clock size={14} className="text-[#B89F7C]" />
          <span className="bg-[#0A0A0A] text-white px-2 py-0.5 text-[10px] font-medium tracking-wider uppercase">
            Précommande
          </span>
        </div>

        <h3 className="font-heading text-xl text-[#0A0A0A] mb-6">
          Récapitulatif
        </h3>

        <div className="space-y-4 mb-6">
          {/* Package */}
          <div className="flex items-start gap-3 pb-4 border-b border-[#E5E5E5]">
            <Package size={18} className="text-[#B89F7C] mt-1" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="font-body text-sm text-[#525252]">Offre</p>
              <p className="font-body font-medium text-[#0A0A0A]">{pkg.name}</p>
              <p className="font-body text-sm text-[#B89F7C]">{pkg.basePrice}€ HT/mois</p>
            </div>
          </div>

          {/* Distribution Tier */}
          {tier && (
            <div className="flex items-start gap-3 pb-4 border-b border-[#E5E5E5]">
              <TrendingUp size={18} className="text-[#B89F7C] mt-1" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="font-body text-sm text-[#525252]">Niveau de diffusion</p>
                <p className="font-body font-medium text-[#0A0A0A]">{tier.name}</p>
                {tierPrice > 0 && (
                  <p className="font-body text-xs text-[#B89F7C]">+{tierPrice}€ HT/mois</p>
                )}
              </div>
            </div>
          )}

          {/* Flacons */}
          <div className="flex items-start gap-3 pb-4 border-b border-[#E5E5E5]">
            <Droplets size={18} className="text-[#B89F7C] mt-1" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="font-body text-sm text-[#525252]">Flacons</p>
              <p className="font-body font-medium text-[#0A0A0A]">
                {pkg.flacons} flacons
              </p>
              <p className="font-body text-xs text-[#525252]">
                {pkg.mlPerFlacon}ML / trimestre
              </p>
            </div>
          </div>

          {/* Configuration */}
          <div className="flex items-start gap-3 pb-4 border-b border-[#E5E5E5]">
            <Settings size={18} className="text-[#B89F7C] mt-1" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="font-body text-sm text-[#525252]">Configuration</p>
              <p className="font-body font-medium text-[#0A0A0A]">
                {numberOfMachines} machine{numberOfMachines > 1 ? "s" : ""}
              </p>
              {machinesCost > 0 && (
                <p className="font-body text-xs text-[#B89F7C]">
                  +{machinesCost}€ HT/mois
                </p>
              )}
            </div>
          </div>

          {/* Engagement */}
          <div className="flex items-start gap-3 pb-4 border-b border-[#E5E5E5]">
            <Calendar size={18} className="text-[#B89F7C] mt-1" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="font-body text-sm text-[#525252]">Engagement</p>
              <p className="font-body font-medium text-[#0A0A0A]">
                {pkg.engagement} mois
              </p>
            </div>
          </div>
        </div>

        {/* Pricing Breakdown */}
        <div className="space-y-2 mb-4">
          <div className="flex justify-between font-body text-sm">
            <span className="text-[#525252]">Abonnement de base</span>
            <span className="text-[#0A0A0A]">{pkg.basePrice}€</span>
          </div>
          {tierPrice > 0 && (
            <div className="flex justify-between font-body text-sm">
              <span className="text-[#525252]">Niveau {tier?.name}</span>
              <span className="text-[#0A0A0A]">+{tierPrice}€</span>
            </div>
          )}
          {machinesCost > 0 && (
            <div className="flex justify-between font-body text-sm">
              <span className="text-[#525252]">Machines add.</span>
              <span className="text-[#0A0A0A]">+{machinesCost}€</span>
            </div>
          )}
          {addOnsCost > 0 && (
            <div className="flex justify-between font-body text-sm">
              <span className="text-[#525252]">Options</span>
              <span className="text-[#0A0A0A]">+{addOnsCost}€</span>
            </div>
          )}
          <div className="flex justify-between font-body text-sm">
            <span className="text-[#525252]">Mise en service</span>
            <span className="text-[#0A0A0A]">{setupFee}€ (unique)</span>
          </div>
        </div>

        {/* Total */}
        <div className="pt-4 border-t border-[#0A0A0A]">
          {promoApplied ? (
            <>
              {/* Promo Applied Display */}
              <div className="bg-[#F0FDF4] border border-[#86EFAC] p-3 mb-3 -mx-3">
                <div className="flex items-center gap-2">
                  <Tag size={14} className="text-[#166534]" />
                  <span className="font-body text-xs font-medium text-[#166534]">
                    {promoCode} : -{promoDiscount}% pendant {promoMonths} mois
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-end mb-1">
                <span className="font-body text-sm text-[#525252]">Prix normal</span>
                <span className="font-body text-lg text-[#525252] line-through">{monthlyPrice}€</span>
              </div>
              <div className="flex justify-between items-end mb-2">
                <span className="font-body text-sm text-[#166534] font-medium">
                  Mois 1-{promoMonths}
                </span>
                <span className="font-heading text-3xl text-[#166534]">{discountedPrice.toFixed(0)}€</span>
              </div>
              <p className="font-body text-xs text-[#525252] mb-2">
                Puis {monthlyPrice}€/mois à partir du mois {promoMonths + 1}
              </p>
            </>
          ) : (
            <>
              <div className="flex justify-between items-end mb-2">
                <span className="font-body text-sm text-[#525252]">Total mensuel HT</span>
                <span className="font-heading text-3xl text-[#0A0A0A]">{monthlyPrice}€</span>
              </div>
            </>
          )}
          <p className="font-body text-xs text-[#525252]">
            À compter de la mise en service
          </p>
        </div>
        
        {/* Deployment info */}
        <div className="mt-4 pt-4 border-t border-[#E5E5E5]">
          <p className="font-body text-xs text-[#525252] flex items-center gap-1">
            <Clock size={12} className="text-[#B89F7C]" />
            Mise en service : 1 à 4 mois
          </p>
        </div>

        {/* Contact phone */}
        <div className="mt-4 pt-4 border-t border-[#E5E5E5]">
          <p className="font-body text-xs text-[#525252] mb-2">Une question ?</p>
          <a 
            href="tel:+33650276753"
            className="flex items-center gap-2 text-[#0A0A0A] hover:text-[#B89F7C] transition-colors"
          >
            <Phone size={14} className="text-[#B89F7C]" />
            <span className="font-body text-sm font-medium">06 50 27 67 53</span>
          </a>
        </div>
      </div>

      {/* Mobile Collapsible Panel */}
      <div
        data-testid="summary-panel-mobile"
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E5E5] z-40"
      >
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between px-6 py-4"
        >
          <div className="flex items-center gap-2">
            <span className="font-body text-sm text-[#525252]">Total mensuel HT</span>
            {promoApplied ? (
              <>
                <span className="font-body text-sm text-[#525252] line-through">{monthlyPrice}€</span>
                <span className="font-heading text-xl text-[#166534]">{discountedPrice.toFixed(0)}€</span>
              </>
            ) : (
              <span className="font-heading text-xl text-[#0A0A0A]">{monthlyPrice}€</span>
            )}
          </div>
          {isExpanded ? (
            <ChevronDown size={20} className="text-[#525252]" />
          ) : (
            <ChevronUp size={20} className="text-[#525252]" />
          )}
        </button>

        {isExpanded && (
          <div className="px-6 pb-6 border-t border-[#E5E5E5] max-h-[50vh] overflow-y-auto">
            <div className="pt-4 space-y-3">
              {promoApplied && (
                <div className="bg-[#F0FDF4] border border-[#86EFAC] p-2 -mx-2 mb-2">
                  <div className="flex items-center gap-2">
                    <Tag size={12} className="text-[#166534]" />
                    <span className="font-body text-xs font-medium text-[#166534]">
                      {promoCode} : -{promoDiscount}% pendant {promoMonths} mois
                    </span>
                  </div>
                </div>
              )}
              <div className="flex justify-between font-body text-sm">
                <span className="text-[#525252]">Offre {pkg.name}</span>
                <span className="text-[#0A0A0A]">{pkg.basePrice}€/mois</span>
              </div>
              {tier && (
                <div className="flex justify-between font-body text-sm">
                  <span className="text-[#525252]">Niveau {tier.name}</span>
                  {tierPrice > 0 ? (
                    <span className="text-[#0A0A0A]">+{tierPrice}€</span>
                  ) : (
                    <span className="text-[#B89F7C]">Inclus</span>
                  )}
                </div>
              )}
              <div className="flex justify-between font-body text-sm">
                <span className="text-[#525252]">{pkg.flacons} flacons</span>
                <span className="text-[#0A0A0A]">Inclus</span>
              </div>
              <div className="flex justify-between font-body text-sm">
                <span className="text-[#525252]">{numberOfMachines} machine{numberOfMachines > 1 ? "s" : ""}</span>
                {machinesCost > 0 && <span className="text-[#0A0A0A]">+{machinesCost}€</span>}
              </div>
              {addOnsCost > 0 && (
                <div className="flex justify-between font-body text-sm">
                  <span className="text-[#525252]">Options</span>
                  <span className="text-[#0A0A0A]">+{addOnsCost}€</span>
                </div>
              )}
              <div className="flex justify-between font-body text-sm">
                <span className="text-[#525252]">Engagement</span>
                <span className="text-[#0A0A0A]">{pkg.engagement} mois</span>
              </div>
              <div className="flex justify-between font-body text-sm pt-2 border-t border-[#E5E5E5]">
                <span className="text-[#525252]">Mise en service</span>
                <span className="text-[#0A0A0A]">{setupFee}€ (unique)</span>
              </div>
              {promoApplied && (
                <p className="font-body text-xs text-[#166534] pt-2">
                  Puis {monthlyPrice}€/mois à partir du mois {promoMonths + 1}
                </p>
              )}
              {/* Contact phone mobile */}
              <div className="pt-3 mt-3 border-t border-[#E5E5E5]">
                <a 
                  href="tel:+33650276753"
                  className="flex items-center justify-center gap-2 text-[#0A0A0A] hover:text-[#B89F7C] transition-colors"
                >
                  <Phone size={14} className="text-[#B89F7C]" />
                  <span className="font-body text-sm">Une question ? 06 50 27 67 53</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SummaryPanel;
